'use client';

import posthog from 'posthog-js';
import { useEffect } from 'react';
import { referenceFor } from './reference-id';

/**
 * Reports the error and returns the reference to show the user.
 *
 * The reference is only worth printing if support can paste it into PostHog and land on
 * the event, so it is always captured as `reference_id` on the exception.
 *
 * It is derived from the error (see `referenceFor`), not generated, so it is correct on
 * the first render and cannot change afterwards - an earlier version generated a random
 * value in an effect, which swapped the code out from under anyone reading it whenever the
 * boundary remounted.
 *
 * Never claim "we've been notified" on a boundary that does not call this.
 */
export const useErrorReport = (error: Error & { digest?: string }, source: string) => {
  const referenceId = referenceFor(error);

  useEffect(() => {
    posthog.captureException(error, {
      source,
      digest: error.digest,
      reference_id: referenceId,
    });
  }, [error, source, referenceId]);

  return referenceId;
};
